import { useEffect, useMemo, useState } from 'react';

import { hashUserId } from '@/lib/hash/userIdHash';

import type { ExtensionScope, TimeExtensionGroup } from './types';

const userHashCache = new Map<string, string>();

type DueDateValue = number | string | null | undefined;

export interface UseEffectiveDueDateParams {
  componentEndTimeUtcMilli: DueDateValue;
  currentUserId: string | null | undefined;
  extensions: TimeExtensionGroup[];
  preLectureEndTimeUtcMilli?: DueDateValue;
  scope: ExtensionScope;
}

export interface UseEffectiveDueDateResult {
  appliedGroupName: string | null;
  effectiveEndTimeUtcMilli: string | null;
  isExtended: boolean;
  isLoadingHash: boolean;
}

interface ComputeEffectiveDueDateParams {
  courseWideEndTimeUtcMilli: DueDateValue;
  currentUserHash: string | null;
  extensions: TimeExtensionGroup[];
}

interface EffectiveDueDateCandidate {
  groupName: string | null;
  millis: number;
  rawValue: string;
}

function parseUtcMillis(value: DueDateValue): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function createCandidate(
  rawValue: DueDateValue,
  groupName: string | null,
): EffectiveDueDateCandidate | null {
  const millis = parseUtcMillis(rawValue);

  if (millis === null) {
    return null;
  }

  return {
    groupName,
    millis,
    rawValue: String(rawValue),
  };
}

async function getCachedUserHash(userId: string): Promise<string> {
  const cachedHash = userHashCache.get(userId);

  if (cachedHash) {
    return cachedHash;
  }

  const hash = await hashUserId(userId);
  userHashCache.set(userId, hash);

  return hash;
}

export function computeEffectiveDueDate({
  courseWideEndTimeUtcMilli,
  currentUserHash,
  extensions,
}: ComputeEffectiveDueDateParams): Omit<UseEffectiveDueDateResult, 'isLoadingHash'> {
  const courseCandidate = createCandidate(courseWideEndTimeUtcMilli, null);
  let latestCandidate = courseCandidate;

  if (currentUserHash) {
    extensions.forEach((extension) => {
      if (!extension.hashedStudentIds.includes(currentUserHash)) {
        return;
      }

      const extensionCandidate = createCandidate(
        extension.endTimeUtcMilli,
        extension.displayName,
      );

      if (
        extensionCandidate &&
        (!latestCandidate || extensionCandidate.millis > latestCandidate.millis)
      ) {
        latestCandidate = extensionCandidate;
      }
    });
  }

  return {
    appliedGroupName: latestCandidate?.groupName ?? null,
    effectiveEndTimeUtcMilli: latestCandidate?.rawValue ?? null,
    isExtended: Boolean(latestCandidate?.groupName),
  };
}

export function useEffectiveDueDate({
  componentEndTimeUtcMilli,
  currentUserId,
  extensions,
  preLectureEndTimeUtcMilli,
  scope,
}: UseEffectiveDueDateParams): UseEffectiveDueDateResult {
  const [currentUserHash, setCurrentUserHash] = useState<string | null>(null);
  const [isLoadingHash, setIsLoadingHash] = useState(false);

  useEffect(() => {
    if (!currentUserId) {
      setCurrentUserHash(null);
      setIsLoadingHash(false);
      return;
    }

    let isMounted = true;
    setIsLoadingHash(true);

    void getCachedUserHash(currentUserId)
      .then((hash) => {
        if (isMounted) {
          setCurrentUserHash(hash);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingHash(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentUserId]);

  const courseWideEndTimeUtcMilli =
    scope === 'pre_lecture'
      ? preLectureEndTimeUtcMilli ?? componentEndTimeUtcMilli
      : componentEndTimeUtcMilli;

  return useMemo(
    () => ({
      ...computeEffectiveDueDate({
        courseWideEndTimeUtcMilli,
        currentUserHash,
        extensions,
      }),
      isLoadingHash,
    }),
    [courseWideEndTimeUtcMilli, currentUserHash, extensions, isLoadingHash],
  );
}
